import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { SettingsOpsService } from '../settings-ops/settings-ops.service';
import { safeJsonInput } from '../../../shared/_utils/safe-json-input';

@Component({
    selector: 'gtw-create-new-settings',
    templateUrl: './create-new-settings.component.html',
    styleUrls: ['./create-new-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateNewSettingsComponent {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  @Input('base-urls')
  set _base_urls(v: string) {
    this.baseURLs = safeJsonInput<any>(v, {}, 'create-new-settings.base-urls') || {};
  }

  @Input('header-data')
  set _header_data(v: string) {
    const header = safeJsonInput<any>(v, {}, 'create-new-settings.header-data') || {};
    this.GENERAL_CONFIG = header?.generalConfigData || {};
  }

  @Input('row-data')
  set _row_data(v: string) {
    this.rowData = safeJsonInput<any>(v, {}, 'create-new-settings.row-data') || {};
    this.title = this.rowData?.title || 'Create New Setting';
    this.settingType = this.rowData?.type || 'configSettings';
    this.client = this.rowData?.client || {};
    this.appKey = this.rowData?.appKey || '';
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    this.filterParams = safeJsonInput<any>(v, {}, 'create-new-settings.filter-params') || {};
  }

  baseURLs: any = {};
  GENERAL_CONFIG: any = {};
  rowData: any = {};
  filterParams: any = {};

  title = 'Create New Setting';
  settingType = 'configSettings';
  client: any = {};
  appKey = '';
  name = '';
  value = '';
  loading = false;

  constructor(
    private settingsOpsService: SettingsOpsService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef
  ) {}

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url || this.baseURLs?.api || '';
  }

  saveSettings(): void {
    if (!this.name) {
      this.gtwSnackbarService.openSnackBar('Name is required', 'gtw-snackbar--yellow');
      return;
    }
    if (!this.baseUrl) {
      this.gtwSnackbarService.openSnackBar('Base URL unavailable', 'gtw-snackbar--red');
      return;
    }
    this.loading = true;
    const payload: any = {
      key: this.name,
      value: this.value,
      clientKey: this.client?.client_key ?? '',
      clientDatasource: this.client?.client_datasource ?? '',
      appKey: this.appKey ?? '',
    };
    const req =
      this.settingType === 'appconfigSettings'
        ? this.settingsOpsService.saveAppSetting(this.baseUrl, payload)
        : this.settingType === 'clientconfigSettings'
          ? this.settingsOpsService.saveClientSetting(this.baseUrl, payload)
          : this.settingsOpsService.saveSystemSetting(this.baseUrl, payload);
    req.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.loading = false;
        if (String(data?.callSuccess) === '1') {
          this.gtwSnackbarService.openSnackBar('Setting saved successfully.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.p_error_out || 'Save failed', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving setting', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
